import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { apiUrlSelector, buildingUuidSelector } from 'store/config/selectors';
import { currentToken } from 'store/user/selectors';

export const usePhotoByUuid = (photoUuid?: string | null | undefined) => {
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const apiUrl = useSelector(apiUrlSelector);
  const token = useSelector(currentToken);
  const buildingUuid = useSelector(buildingUuidSelector);
  const currentPhotoUrl = useRef<string | null>(null);

  const photoUrl = useMemo(() => {
    if (!photoBlob) {
      if (currentPhotoUrl.current) {
        URL.revokeObjectURL(currentPhotoUrl.current);
        currentPhotoUrl.current = null;
      }
      return null;
    }

    if (currentPhotoUrl.current) {
      URL.revokeObjectURL(currentPhotoUrl.current);
    }

    const newUrl = URL.createObjectURL(photoBlob);
    currentPhotoUrl.current = newUrl;
    return newUrl;
  }, [photoBlob]);

  useEffect(() => {
    const fetchPhoto = async () => {
      if (!photoUuid || !buildingUuid || !token) {
        setPhotoBlob(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const response = await fetch(
          `${apiUrl}/visitor-registration/v1/locations/${buildingUuid}/visitor/photos/${photoUuid}`,
          {
            headers: {
              Authorization: token,
            },
          },
        );

        if (response.ok) {
          const blob = await response.blob();
          setPhotoBlob(blob);
        }
      } catch {
        // Photo fetch failed, will show fallback
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhoto();
  }, [photoUuid, apiUrl, token, buildingUuid]);

  useEffect(
    () => () => {
      if (currentPhotoUrl.current) {
        URL.revokeObjectURL(currentPhotoUrl.current);
      }
    },
    [],
  );

  return { photoUrl, isLoading };
};
